import { useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { artifactsKeys } from "@/hooks/useArtifacts";
import {
  useAdsWorkspaceStore,
  persistAdArtifact,
  type BananImageResult,
  type GeneratedAd,
  type KlingVideoResult,
  type SubAgentId,
} from "@/features/ads";

/**
 * Short deterministic hash of a media URL. Used to derive a stable
 * `result.id` so repeated emissions of the same generated media collapse in
 * the ads store and the backend artifact_save endpoint.
 */
function hashMediaUrl(url: string): string {
  let h = 5381;
  for (let i = 0; i < url.length; i += 1) {
    h = ((h << 5) + h) ^ url.charCodeAt(i);
  }
  return (h >>> 0).toString(36);
}

export function useSubAgentGeneratedAssets(agentId: SubAgentId, initialSessionId: string | null) {
  const queryClient = useQueryClient();
  const handledImageUrlsRef = useRef<Set<string>>(new Set());
  const handledVideoUrlsRef = useRef<Set<string>>(new Set());
  const liveSessionIdRef = useRef<string | null>(initialSessionId);

  useEffect(() => {
    liveSessionIdRef.current = initialSessionId;
  }, [initialSessionId]);

  const setLiveSessionId = useCallback((sessionId: string | null) => {
    liveSessionIdRef.current = sessionId;
  }, []);

  const handleImageGenerated = useCallback(
    (imageUrl: string) => {
      if (handledImageUrlsRef.current.has(imageUrl)) return;
      handledImageUrlsRef.current.add(imageUrl);

      const store = useAdsWorkspaceStore.getState();
      const resultId = `banan-direct-${hashMediaUrl(imageUrl)}`;
      const result: BananImageResult = {
        id: resultId,
        status: "done",
        output: imageUrl,
        created_at: new Date().toISOString(),
      };
      const metadata: Record<string, unknown> = { source: "banan-direct", agentId };
      const sid = liveSessionIdRef.current;
      if (sid) metadata.sessionId = sid;
      const newAd: GeneratedAd = {
        type: "image",
        result,
        metadata,
      };
      store.mergeAds([newAd]);
      store.setActiveTab("assets");

      const workspaceId = store.scopeWorkspaceId;
      if (workspaceId) {
        void persistAdArtifact({
          adType: "image",
          result,
          workspaceId,
          label: "Agent Banan (direct)",
        });
      }
    },
    [agentId],
  );

  const handleVideoGenerated = useCallback(
    (videoUrl: string, thumbnailUrl?: string) => {
      if (handledVideoUrlsRef.current.has(videoUrl)) return;
      handledVideoUrlsRef.current.add(videoUrl);

      const isHiggsfield = agentId === "higgsfield";
      const isHiggsfieldCli = agentId === "higgsfield_cli";
      const isHeyGen = agentId === "agent_v";
      const generator: "kling" | "higgsfield" | "higgsfield_cli" | "heygen" =
        isHeyGen
          ? "heygen"
          : isHiggsfieldCli
            ? "higgsfield_cli"
            : isHiggsfield
              ? "higgsfield"
              : "kling";
      const idPrefix = isHiggsfieldCli
        ? "higgsfield-cli-direct"
        : isHiggsfield
          ? "higgsfield-direct"
          : isHeyGen
            ? "heygen-direct"
            : "vid-direct";
      const persistLabel = isHiggsfieldCli
        ? "Agent Higgs (direct)"
        : isHiggsfield
          ? "Agent Vidzz (direct)"
          : isHeyGen
            ? "Agent V (direct)"
            : "Agent Vid (direct)";

      const store = useAdsWorkspaceStore.getState();
      const resultId = `${idPrefix}-${hashMediaUrl(videoUrl)}`;
      const result: KlingVideoResult = {
        id: resultId,
        status: "done",
        video_output: videoUrl,
        video_thumbnail: thumbnailUrl,
      };
      const metadata: Record<string, unknown> = {
        source: idPrefix,
        agentId,
        assetKind: "video-ad",
        generator,
      };
      const sid = liveSessionIdRef.current;
      if (sid) metadata.sessionId = sid;
      const newAd: GeneratedAd = {
        type: "video",
        result,
        metadata,
      };
      store.mergeAds([newAd]);
      store.setActiveTab("assets");

      const workspaceId = store.scopeWorkspaceId;
      // Agent V persists final videos server-side; invalidate so /projects
      // swaps the optimistic session asset for the canonical artifact row.
      if (workspaceId && isHeyGen) {
        void queryClient.invalidateQueries({ queryKey: artifactsKeys.all });
      } else if (workspaceId) {
        void persistAdArtifact({
          adType: "video",
          result,
          workspaceId,
          label: persistLabel,
          extraMetadata: { generator, assetKind: "video-ad" },
        });
      }
    },
    [agentId, queryClient],
  );

  return {
    handleImageGenerated,
    handleVideoGenerated,
    setLiveSessionId,
  };
}
